import DataTable from 'components/ui/DataTable/data-table';
import Modal from 'components/ui/Modal/modal';
import PrimaryButton from 'components/ui/PrimaryButton';
import SecondaryButton from 'components/ui/SecondaryButton';
import { stat } from 'fs';
import moment from 'moment';
import React, { FC, useState } from 'react';
import { Cell } from 'react-table';
import { toast } from 'react-toastify';
import {
  ITraineeDetail,
  useListTrainingApplicationsQuery,
  useUpdateTrainingApplicationMutation
} from 'redux/reducers/user';

interface ITrainingRequestTable {
  tableHeaders?: () => React.ReactNode;
  allID?: string[]; // optional if you still need bulk
  setAllID?: React.Dispatch<React.SetStateAction<string[]>>;
}

const TrainingRequestTable: FC<ITrainingRequestTable> = ({ tableHeaders }) => {
  const [userDetail, setUserDetail] = useState<any | null>(null);
  const [actionType, setActionType] = useState<'1' | '2' | null>(null);

  const { data: applications = [], isLoading } = useListTrainingApplicationsQuery();
  const [updateApplication, { isLoading: isUpdating }] = useUpdateTrainingApplicationMutation();

  const handleAction = async () => {
    if (!userDetail || !actionType) return;

    try {
      await updateApplication({
        id: userDetail.id.toString(),
        status: actionType
      }).unwrap();

      toast.success(`Trainee ${actionType === '1' ? 'approved' : 'rejected'} successfully!`);
      setUserDetail(null);
      setActionType(null);
    } catch (error) {
      toast.error(`Failed to ${actionType} trainee!`);
    }
  };

  const openModal = (trainee: ITraineeDetail, type: '1' | '2') => {
    setUserDetail(trainee);
    setActionType(type);
  };

  const closeModal = () => {
    setUserDetail(null);
    setActionType(null);
  };

  const columns = [
    {
      Header: 'S.N',
      accessor: 'sn',
      Cell: ({ row }: Cell<ITraineeDetail>) => row.index + 1
    },
    {
      Header: 'Trainee Name',
      accessor: 'trainee_name'
    },

    {
      Header: 'Routine',
      accessor: 'routine_name'
    },
    {
      Header: 'Date',
      accessor: 'applied_date',
      Cell: ({ row }: Cell<any>) => {
        const status = row.original?.applied_date;
        return <span className="">{status ? moment(status).format('YYYY-MM-DD') : ''}</span>;
      }
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }: Cell<any>) => {
        const status = row.original?.status;
        return (
          <span className="text-warning">
            {status == 0 ? 'Pending' : status == '1' ? 'Approve' : 'Rejected'}
          </span>
        );
      }
    },
    {
      Header: 'Action',
      Cell: ({ row }: Cell<any>) => {
        const trainee = row.original;

        return (
          <>
            <PrimaryButton
              title="Approve"
              type="button"
              small="btn-sm mx-2"
              onClick={() => {
                if (trainee?.status == '1') return toast.warn('Already approved');
                openModal(trainee, '1');
              }}
            />

            <PrimaryButton
              isDanger
              title="Reject"
              type="button"
              small="btn-sm"
              onClick={() => {
                if (trainee?.status == '2') return toast.warn('Already rejected');

                openModal(trainee, '2');
              }}
            />
          </>
        );
      }
    }
  ];

  return (
    <div>
      <DataTable
        columns={columns}
        data={applications}
        isLoading={isLoading}
        tableHeaders={tableHeaders}
      />

      {/* Unified Approve/Reject Confirmation Modal */}
      <Modal
        title={actionType === '1' ? 'Approve Trainee' : 'Reject Trainee'}
        isModalOpen={!!userDetail && !!actionType}
        closeModal={closeModal}
        modalSize="modal-md"
        renderFooter={() => (
          <div className="hstack gap-2 justify-content-end">
            <SecondaryButton onClick={closeModal} title="Cancel" />
            <PrimaryButton
              type="button"
              title={actionType === '1' ? 'Approve' : 'Reject'}
              isDanger={actionType === '2'}
              onClick={handleAction}
              isLoading={isUpdating}
            />
          </div>
        )}>
        <h4 className="fw-bold">
          Are you sure you want to {actionType === '1' ? 'Approve' : 'Reject'} the training
          application of <span className="text-primary">{`${userDetail?.trainee_name ?? ''}`}</span>
          ?
        </h4>
      </Modal>
    </div>
  );
};

export default TrainingRequestTable;
